import { ForgeTooltip } from '@tylertech/forge-react';
import { TABLE_COLUMN_WIDTH } from '../../static/constants.js';
import TableCellButton from '../reusable/AgGridCells/TableCellButton/TableCellButton.js';
import CustomTooltip from '../System/AgGrid/CustomTooltip.js';
import React from 'react';
import TruncatedCellRenderer, { tooltipValueGetter } from '../reusable/AgGridCells/TruncatedCellRenderer/TruncatedCellRenderer.js';
import { normalizePythonQuestion } from '../PBBStrategist/ChatbotAdapter/pbbStrategistAdapterHelpers.js';
import { AGGridDateComparator } from '../System/AgGrid/RegisterAgGridModules.js';

export const AI_ANALYTICS_SLOTS = {
  LABEL: 'label',
  END: 'end',
  COUNT: 'count'
};
export const NULLABLE_STATUS = 'none';
export const TIME_PERIOD_LABEL = 'Time period';
export const MODAL_TITLE = 'History of chat';
export const FIRST_TAB_TABLE_HEADER = 'Top organizations';
export const SECOND_TAB_TABLE_HEADER = 'Questions';

export const getAiAnalyticsTabButtons = () => {
  return [{ name: 'PBB strategist overview' }, { name: 'Chat history' }];
};

export const getCardTitle = (activeTab) => {
  if (activeTab === 0) return 'Analytics';
  return 'Current chats';
};

export const SELECT_OPTIONS = [{ label: 'Last 24 hours', value: 1 }, { label: 'Last 10 days', value: 10 }, { label: 'Last 30 days', value: 30 }, { label: 'Last 60 days', value: 60 }];

export const defaultColumnDef = {
  editable: false,
  floatingFilter: true,
  sortable: true,
  filter: true,
  resizable: true,
  enableCellChangeFlash: false,
  tooltipComponent: CustomTooltip,
  lockPinned: true,
};

export const getPbbStrategistOverviewColumns = () => {
  return [
    {
      headerName: '#',
      headerTooltip: 'ID',
      field: 'organizationId',
      tooltipField: 'organizationId',
      context: 'organizationId',
    },
    {
      headerName: 'Organization',
      headerTooltip: 'Organization',
      field: 'organizationName',
      tooltipField: 'Organization',
      context: 'Organization',
    },
    {
      headerName: 'Questions',
      headerTooltip: 'Questions',
      field: 'currentAnswers',
      tooltipField: 'Questions',
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
    },
    {
      headerName: 'Users',
      headerTooltip: 'Users',
      field: 'totalUsers',
      tooltipField: 'Users',
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
    }
  ];
};

export const getStatusBadgeTheme = (status) => {
  return {
    positive: 'success',
    none: 'info',
    negative: 'error',
  }[status];
};

export const getChatHistoryColumns = (handleActionButtonClick) => {
  return [
    {
      colId: 'status',
      headerName: 'Status',
      headerTooltip: 'Status',
      tooltipValueGetter: tooltipValueGetter,
      field: 'status',
      valueFormatter: (params) => {
        return params.value || NULLABLE_STATUS;
      },
      cellRenderer: TruncatedCellRenderer,
      cellRendererParams: (params) => ({
        main: params.value || NULLABLE_STATUS,
        number: 0,
        value: params.value || NULLABLE_STATUS,
        theme: getStatusBadgeTheme(params.value || NULLABLE_STATUS),
        type: 'badge',
      }),
      sortable: true,
      context: 'Status',
      width: TABLE_COLUMN_WIDTH.XS
    },
    {
      colId: '2',
      headerName: 'User',
      headerTooltip: 'User',
      tooltipField: 'user',
      field: 'user',
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      colId: '3',
      headerName: 'Organization',
      headerTooltip: 'Organization',
      tooltipField: 'organizationName',
      field: 'organizationName',
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      colId: '4',
      context: 'Date',
      headerName: 'Date',
      headerTooltip: 'Date',
      field: 'date',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        if (!params.data?.date) return '';

        const cleanDateStr = String(params.data.date).replace(/\s+/g, '');
        const dateObj = new Date(cleanDateStr);

        if (isNaN(dateObj.getTime())) return params.data.date;

        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = String(dateObj.getFullYear()).substring(2);

        return `${month}/${day}/${year}`;
      },
      tooltipValueGetter: (params) => params.value,
      comparator: AGGridDateComparator,
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      colId: '5',
      headerName: 'Question',
      headerTooltip: 'Question',
      field: 'question',
      valueGetter: (params) => normalizePythonQuestion(params?.data?.question),
      tooltipValueGetter: (params) => params.value,
      width: TABLE_COLUMN_WIDTH.XXL
    },
    {
      colId: '6',
      headerName: 'Feedback',
      headerTooltip: 'Feedback',
      tooltipField: 'feedback',
      field: 'feedback',
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      colId: 'action',
      headerName: 'Action',
      headerTooltip: 'Action',
      field: '',
      filter: false,
      suppressFloatingFilterButton: true,
      cellRenderer: (params) => {
        return (
          <>
            <TableCellButton
              id={'budget-create-request-button'}
              label={'Item info'}
              data={params.data}
              icon={'chevron_right'}
              handleClick={() => handleActionButtonClick(params.data)}
            />
            <ForgeTooltip anchor="budget-create-request-button" placement={'bottom'}>
              Review chat messages
            </ForgeTooltip>
          </>
        );
      },
      cellClass: 'grid-cell-centered',
      flex: 0.5,
      sortable: false,
      pinned: 'right',
      context: 'Action',
      hide: false,
      suppressColumnsToolPanel: true,
      width: TABLE_COLUMN_WIDTH.XS,
    }
  ];
};

const formatMsToHHMMSS = (ms) => {
  if (!ms || isNaN(ms)) return '00:00:00';

  const totalSeconds = Math.floor(Number(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (num) => String(num).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const mapCardsData = (data) => {
  return {
    FIRST_ROW: [
      {
        label: 'Total users',
        count: data.totalUsers?.toLocaleString() || '0'
      },
      {
        label: 'Active users',
        count: data.totalActiveUsers?.toLocaleString() || '0'
      },
      {
        label: 'Average response time',
        count: formatMsToHHMMSS(data?.totalAverageResponseTime)
      }
    ],
    SECOND_ROW: [
      {
        label: 'Total organizations',
        count: data.totalOrganizations?.toLocaleString() || '0'
      },
      {
        label: 'Current answers',
        count: data.totalCurrentAnswers?.toLocaleString() || '0'
      },
      {
        label: 'Thumbs up',
        count: data.totalThumbsUp?.toLocaleString() || '0'
      },
      {
        label: 'Thumbs down',
        count: data.totalThumbsDown?.toLocaleString() || '0'
      }
    ]
  };
};
